import { Metamodel } from 'app/model/base/model';
import { DataFlowDatasetSettings, DatasetFormatType } from './dataflow-dataset-settings';
import { DataFlowExpression } from '../dataflowexpression';
import { Owned } from 'app/model/base/named';
import { FileBasedSettings } from './file-based-settings';
import { ParquetSettings } from './parquet-settings';
import { DeltaSettings } from './delta-dataset-settings';
import { DataFlowSink } from '../dataflow-sink';

import { DataFlowSource } from '../dataflow-source';
import { AzureBlobFSLinkedService } from 'app/model/linkedservice/azure-blob-fs.linkedservice';
import { GitHubLinkedService } from 'app/model/linkedservice/github.linkedservice';
import { validateLinkedService } from '../transformation-dataset-validator';
import { Constants } from 'app/shared/shared-constants';
import { format, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { BooleanDataFlowType } from '../dataflow-datatype';
import { DelimitedTextSettings } from './delimited-text-settings';
import { adfModelTypeMap } from '../../../registries/consts/adf-model-type-map-const';
import { IViolation } from 'app/model/base/annotation';
import { GlobalVariables } from '../../../services/globals';
import { LoggerService } from 'app/logger/logger.service';
import { IValidationContext } from 'shell-models-lib';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';

export enum CdmManifestType {
    MANIFEST = 'manifest',
    MODEL = 'model'
}

export enum EntityReferenceType { LOCAL, CUSTOM, STANDARD }

@Metamodel.model({
    name: adfModelTypeMap.cdm.dataflowSettings,
    validator: CdmSettings.validateCdmSettings
})
export class CdmSettings extends DataFlowDatasetSettings {
    @Metamodel.attribute({
    })
    public corpusPath: DataFlowExpression;

    @Metamodel.attribute({
    })
    public entity: DataFlowExpression;

    @Metamodel.attribute({
    })
    public entityPath: DataFlowExpression;

    @Metamodel.attribute({
    })
    public manifestName: DataFlowExpression;

    @Metamodel.attribute({
    })
    public manifestType: CdmManifestType = CdmManifestType.MANIFEST;

    @Metamodel.attribute({
    })
    public partitionPath: DataFlowExpression;

    @Metamodel.attribute({
    })
    public container: DataFlowExpression;

    @Metamodel.attribute({
    })
    public repository: DataFlowExpression;

    @Metamodel.attribute({
    })
    public branch: DataFlowExpression;

    @Metamodel.attribute({
    })
    public truncate: DataFlowExpression;
    public subFormatSettings: FileBasedSettings;
    public entityReferenceType: EntityReferenceType;

    public get includeSchemaLinkedServiceInResource(): boolean {
        return this.isCustomEntityReference;
    }

    private _cachedSubFormatSettings = new Map<string, FileBasedSettings>();

    public static async validateCdmSettings(settings: CdmSettings, context?: IValidationContext): Promise<IViolation[]> {
        const violations: IViolation[] = [];
        if (settings.transform.isInlineDataset && settings.transform.datasetSettings && settings.transform.datasetSettings.getFormat() === DatasetFormatType.CDM) {
            if (settings.transform instanceof DataFlowSink) {
                if (settings.isCustomEntityReference && stringIsNullOrEmpty(settings.corpusPath.expression)) {
                    violations.push({
                        context: settings.transform,
                        message: () => format(stringResources.RequiredTemplate, stringResources.DataFlow_Corpus)
                    });
                }
            }
            if (settings.subFormatSettings) {
                if (stringIsNullOrEmpty(settings.subFormatSettings.container.expression)) {
                    violations.push({
                        message: () => format(stringResources.RequiredTemplate, stringResources.ModelName_Container),
                        redirectTo: {
                            entity: settings.transform
                        }
                    });
                } else if (GlobalVariables.containerValidation && !Constants.slashSpaceValidation.test(settings.subFormatSettings.container.expression) &&
                    settings.subFormatSettings.container.expression !== settings.subFormatSettings.container.templateExpression) {
                    const errorMsg = format(stringResources.Dataset_FileName_SpaceSlashValidation, stringResources.ModelName_Container);
                    const logger = (await getServiceInstance<LoggerService>(context.contextData, 'LoggerService'));
                    logger.error('CdmSettings', errorMsg);

                    violations.push({
                        redirectTo: {
                            entity: settings.transform
                        },
                        message: () => errorMsg
                    });
                }
            }

            if (stringIsNullOrEmpty(settings.entity.expression)) {
                violations.push({
                    context: settings.transform,
                    message: () => format(stringResources.RequiredTemplate, stringResources.DataFlow_Entity)
                });
            }
            if (settings.isCustomEntityReference && settings.manifestType === CdmManifestType.MANIFEST) {
                if (!settings.transform.schemaLinkedService) {
                    violations.push({
                        message: () => format(stringResources.RequiredTemplate, stringResources.SchemaLinkedService),
                        context: settings.transform
                    });
                } else {
                    if (settings.transform.schemaLinkedService instanceof AzureBlobFSLinkedService) {
                        if (stringIsNullOrEmpty(settings.container.expression)) {
                            violations.push({
                                message: () => format(stringResources.RequiredTemplate, stringResources.ModelName_Container),
                                redirectTo: {
                                    entity: settings.transform
                                }
                            });
                        }
                    } else if (settings.transform.schemaLinkedService instanceof GitHubLinkedService) {
                        if (stringIsNullOrEmpty(settings.repository.expression)) {
                            violations.push({
                                message: () => format(stringResources.RequiredTemplate, stringResources.Repository),
                                redirectTo: {
                                    entity: settings.transform
                                }
                            });
                        }
                        if (stringIsNullOrEmpty(settings.branch.expression)) {
                            violations.push({
                                message: () => format(stringResources.RequiredTemplate, stringResources.Branch),
                                redirectTo: {
                                    entity: settings.transform
                                }
                            });
                        }
                    }
                    violations.push(...(await validateLinkedService(settings.transform, settings.transform.schemaLinkedService, undefined, context)));
                }
            }
        }

        return violations;
    }

    public get transform(): DataFlowSource | DataFlowSink {
        return this.owner instanceof DataFlowSource ? this.owner as DataFlowSource : this.owner as DataFlowSink;
    }

    public get isCustomEntityReference(): boolean {
        return this.manifestType === CdmManifestType.MANIFEST && this.entityReferenceType === EntityReferenceType.CUSTOM;
    }

    public get isLocalEntityReference(): boolean {
        return this.manifestType === CdmManifestType.MANIFEST && this.entityReferenceType === EntityReferenceType.LOCAL;
    }

    public get isStandardEntityReference(): boolean {
        return this.manifestType === CdmManifestType.MANIFEST && this.entityReferenceType === EntityReferenceType.STANDARD;
    }

    public getFormat(): DatasetFormatType {
        return DatasetFormatType.CDM;
    }

    public getSubFormat(): string {
        return this.subFormatSettings ? this.subFormatSettings.getFormat() : undefined;
    }

    public getDatasetType(): string {
        return undefined;
    }

    public getIconClass(): string {
        return 'i-20x20-cdm';
    }

    public initialize(subFormat: string): void {
        this.corpusPath = this.transform.createTemplatableExpression();
        this.entity = this.transform.createTemplatableExpression();
        this.manifestName = this.transform.createTemplatableExpression();
        this.partitionPath = this.transform.createTemplatableExpression();
        this.entityPath = this.transform.createTemplatableExpression();
        this.container = this.transform.createTemplatableExpression();
        this.repository = this.transform.createTemplatableExpression();
        this.branch = this.transform.createTemplatableExpression();
        this.truncate = this.transform.createTemplatableExpression(BooleanDataFlowType.TYPE, 'false');
        this.subFormatSettings = Owned.create<FileBasedSettings>(FileBasedSettings.MODEL, this.transform);
        this.entityReferenceType = this.transform instanceof DataFlowSource ? EntityReferenceType.LOCAL : EntityReferenceType.CUSTOM;
        if (subFormat === DatasetFormatType.Delimited) {
            this.subFormatSettings = Owned.create<DelimitedTextSettings>(DelimitedTextSettings.MODEL, this.transform);
            this.subFormatSettings.initialize('');
        } else if (subFormat === DatasetFormatType.Parquet) {
            this.subFormatSettings = Owned.create<ParquetSettings>(ParquetSettings.MODEL, this.transform);
            this.subFormatSettings.initialize('');
        } else if (subFormat === DatasetFormatType.Delta) {
            this.subFormatSettings = Owned.create<DeltaSettings>(DeltaSettings.MODEL, this.transform);
            this.subFormatSettings.initialize('');
        }
    }

    public changeSubFormatSettingsType(subFormat: string): void {
        const existingSubFormatSettings = this._cachedSubFormatSettings.get(subFormat);
        if (existingSubFormatSettings) {
            this.subFormatSettings = existingSubFormatSettings;
        } else {
            this._createSubFormatSettings(subFormat);
        }
    }

    private _createSubFormatSettings(subFormat: string): void {
        if (subFormat === DatasetFormatType.Delimited || subFormat === DatasetFormatType.Parquet || subFormat === DatasetFormatType.Delta) {
            let newSubformatSettings;
            switch (subFormat) {
                case DatasetFormatType.Delimited: {
                    newSubformatSettings = Owned.create<DelimitedTextSettings>(DelimitedTextSettings.MODEL, this.transform);
                    break;
                }
                case DatasetFormatType.Parquet: {
                    newSubformatSettings = Owned.create<ParquetSettings>(ParquetSettings.MODEL, this.transform);
                    break;
                }
                case DatasetFormatType.Delta: {
                    newSubformatSettings = Owned.create<DeltaSettings>(DeltaSettings.MODEL, this.transform);
                    break;
                }
                default: {
                    newSubformatSettings = Owned.create<DelimitedTextSettings>(DelimitedTextSettings.MODEL, this.transform);
                    break;
                }
            }
            newSubformatSettings.initialize('');
            newSubformatSettings.folderPath.expression = this.subFormatSettings.folderPath.expression;
            newSubformatSettings.container.expression = this.subFormatSettings.container.expression;
            this.subFormatSettings = newSubformatSettings;
            this._cachedSubFormatSettings.set(subFormat, this.subFormatSettings);
        }
    }
}
